import {
  AgreeToTermsPolicy,
  type ExperienceSocialConnector,
  type SignInIdentifier,
} from "@logto/schemas";
import useDirectSocialSignIn from "@/hooks/use-direct-social-sign-in";
import { AgreeToTermsPolicy, type SignInIdentifier } from "@logto/schemas";
import classNames from "classnames";
import { useCallback, useContext, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";

import UserInteractionContext from "@/Providers/UserInteractionContextProvider/UserInteractionContext";
import LockIcon from "@/assets/icons/lock.svg?react";
import Button from "@/components/Button";
import ErrorMessage from "@/components/ErrorMessage";
import { SmartInputField, PasswordInputField } from "@/components/InputFields";
import type { IdentifierInputValue } from "@/components/InputFields/SmartInputField";
import CaptchaBox from "@/containers/CaptchaBox";
import ForgotPasswordLink from "@/containers/ForgotPasswordLink";
import TermsAndPrivacyCheckbox from "@/containers/TermsAndPrivacyCheckbox";
import usePasswordSignIn from "@/hooks/use-password-sign-in";
import usePrefilledIdentifier from "@/hooks/use-prefilled-identifier";
import { useForgotPasswordSettings } from "@/hooks/use-sie";
import useSingleSignOnWatch from "@/hooks/use-single-sign-on-watch";
import useTerms from "@/hooks/use-terms";
import {
  getGeneralIdentifierErrorMessage,
  validateIdentifierField,
} from "@/utils/form";

import styles from "./index.module.scss";

type Props = {
  readonly className?: string;
  // eslint-disable-next-line react/boolean-prop-naming
  readonly autoFocus?: boolean;
  readonly signInMethods: SignInIdentifier[];
  readonly directSocialConnectors?: ExperienceSocialConnector[];
};

export type FormState = {
  identifier: IdentifierInputValue;
  password: string;
};

const PasswordSignInForm = ({
  className,
  autoFocus,
  signInMethods,
  directSocialConnectors = []
}: Props) => {
    const {
    t,
    i18n: { language },
  } = useTranslation();

  const { errorMessage, clearErrorMessage, onSubmit } = usePasswordSignIn();
  const { isForgotPasswordEnabled } = useForgotPasswordSettings();
  const { termsValidation, agreeToTermsPolicy } = useTerms();
  const { loadingConnectorId, signIn: signInWithConnector } = useDirectSocialSignIn();
  const { setIdentifierInputValue } = useContext(UserInteractionContext);
  const prefilledIdentifier = usePrefilledIdentifier({
    enabledIdentifiers: signInMethods,
  });

  const {
    watch,
    register,
    handleSubmit,
    control,
    formState: { errors, isValid, isSubmitting },
  } = useForm<FormState>({
    reValidateMode: "onBlur",
    defaultValues: {
      identifier: prefilledIdentifier,
      password: "",
    },
  });

  const { showSingleSignOnForm, navigateToSingleSignOn } = useSingleSignOnWatch(
    watch("identifier")
  );

  const submitWith = useCallback(
    (
      executor: (payload: {
        identifier: IdentifierInputValue;
        password: string;
      }) => Promise<void>
    ) =>
      handleSubmit(async ({ identifier, password }) => {
        const { type, value } = identifier;

        if (!type || !value) {
          return;
        }

        setIdentifierInputValue({ type, value });

        if (showSingleSignOnForm) {
          await navigateToSingleSignOn();
          return;
        }

        if (agreeToTermsPolicy === AgreeToTermsPolicy.Manual && !(await termsValidation())) {
          return;
        }

        await executor({ identifier, password });
      }),
    [
      agreeToTermsPolicy,
      handleSubmit,
      navigateToSingleSignOn,
      setIdentifierInputValue,
      showSingleSignOnForm,
      termsValidation,
    ]
  );

  const onSubmitHandler = useCallback(
    async (event?: React.FormEvent<HTMLFormElement>) => {
      clearErrorMessage();

      await handleSubmit(async ({ identifier: { type, value }, password }) => {
        if (!type) {
          return;
        }

        setIdentifierInputValue({ type, value });

        if (showSingleSignOnForm) {
          await navigateToSingleSignOn();
          return;
        }

        // Check if the user has agreed to the terms and privacy policy before signing in when the policy is set to `Manual`
        if (
          agreeToTermsPolicy === AgreeToTermsPolicy.Manual &&
          !(await termsValidation())
        ) {
          return;
        }

        await onSubmit({
          identifier: { type, value },
          password,
        });
      })(event);
    },
    [
      agreeToTermsPolicy,
      clearErrorMessage,
      handleSubmit,
      navigateToSingleSignOn,
      onSubmit,
      setIdentifierInputValue,
      showSingleSignOnForm,
      termsValidation,
    ]
  );

  const handleDirectConnectorClick = useCallback(
    async (connector: ExperienceSocialConnector) => {
      clearErrorMessage();

      await submitWith(async ({ identifier: { value }, password }) => {
        await signInWithConnector(connector, { username: value ?? '', password });
      })();
    },
    [clearErrorMessage, signInWithConnector, submitWith]
  );


  useEffect(() => {
    if (!isValid) {
      clearErrorMessage();
    }
  }, [clearErrorMessage, isValid]);

  return (
    <form
      className={classNames(styles.form, className)}
      onSubmit={onSubmitHandler}
    >
      <Controller
        control={control}
        name="identifier"
        rules={{
          validate: ({ type, value }) => {
            if (!type || !value) {
              return getGeneralIdentifierErrorMessage(
                signInMethods,
                "required"
              );
            }

            const errorMessage = validateIdentifierField(type, value);

            return errorMessage
              ? getGeneralIdentifierErrorMessage(signInMethods, "invalid")
              : true;
          },
        }}
        render={({ field, formState: { defaultValues } }) => (
          <SmartInputField
            autoFocus={autoFocus}
            className={styles.inputField}
            {...field}
            isDanger={!!errors.identifier}
            errorMessage={errors.identifier?.message}
            enabledTypes={signInMethods}
            defaultValue={defaultValues?.identifier?.value}
          />
        )}
      />
      {showSingleSignOnForm && (
        <div className={styles.message}>
          {t("description.single_sign_on_enabled")}
        </div>
      )}

      {!showSingleSignOnForm && (
        <PasswordInputField
          className={styles.inputField}
          autoComplete="current-password"
          label={t("input.password")}
          isDanger={!!errors.password}
          errorMessage={errors.password?.message}
          {...register("password", { required: t("error.password_required") })}
        />
      )}

      {errorMessage && (
        <ErrorMessage className={styles.formErrors}>
          {errorMessage}
        </ErrorMessage>
      )}

      {isForgotPasswordEnabled && !showSingleSignOnForm && (
        <ForgotPasswordLink
          className={styles.link}
          identifier={watch("identifier").type}
          value={watch("identifier").value}
        />
      )}

      {/**
       * Have to use css to hide the terms element.
       * Remove element from dom will trigger a form re-render.
       * Form rerender will trigger autofill.
       * If the autofill value is SSO enabled, it will always show SSO form.
       */}
      <TermsAndPrivacyCheckbox
        className={classNames(
          styles.terms,
          // For sign in, only show the terms checkbox if the terms policy is manual
          agreeToTermsPolicy !== AgreeToTermsPolicy.Manual && styles.hidden
        )}
      />

      <CaptchaBox />

      <Button
        name="submit"
        title={
          showSingleSignOnForm ? "action.single_sign_on" : "action.sign_in"
        }
        icon={showSingleSignOnForm ? <LockIcon /> : undefined}
        htmlType="submit"
        isLoading={isSubmitting}
      />

      <input hidden type="submit" />

       {directSocialConnectors.length > 0 && !showSingleSignOnForm && (
          //-- Custom : Feature AD/LDAP [[
        <div className={styles.directConnectors}>
          {directSocialConnectors.map((connector) => {
            const localName = connector.name[language] ?? connector.name.en;

            return (
              <Button
                key={connector.id}
                type="secondary"
                title="action.sign_in_with"
                i18nProps={{ name: localName }}
                isLoading={loadingConnectorId === connector.id}
                onClick={(event) => {
                  event.preventDefault();
                  void handleDirectConnectorClick(connector);
                }}
              />
            );
              //-- ]]
          })}
        </div>
      )}
    </form>
  );
};

export default PasswordSignInForm;
